<script>
  import { getValidationSummary, autoFixQuestionCompliance } from './relationshipValidator.js';
  
  export let question;
  export let updateWorkingQuestion;
  export let showAutoFix = true;
  export let expanded = false;
  
  let validationSummary = null;
  let fixes = [];
  
  function validateQuestion() {
    validationSummary = getValidationSummary(question);
    fixes = autoFixQuestionCompliance(question);
  }
  
  function applyFix(fix) {
    if (fix.canAutoFix && fix.fix) {
      const fixedQuestion = fix.fix();
      // Apply all changes from the fixed question
      Object.keys(fixedQuestion).forEach(key => {
        if (fixedQuestion[key] !== question[key]) {
          updateWorkingQuestion(key, fixedQuestion[key]);
        }
      });
      
      // Remove any keys that were deleted
      Object.keys(question).forEach(key => {
        if (!(key in fixedQuestion)) {
          updateWorkingQuestion(key, undefined);
        }
      });
      
      // Re-validate after fix
      setTimeout(validateQuestion, 100);
    }
  }
  
  function applyAllFixes() {
    fixes.filter(fix => fix.canAutoFix).forEach(fix => {
      applyFix(fix);
    });
  }
  
  function toggleExpanded() {
    expanded = !expanded;
  }
  
  // Validate whenever question changes
  $: question, validateQuestion();
  $: hasErrors = fixes.some(fix => fix.type === 'error');
  $: hasWarnings = fixes.some(fix => fix.type !== 'error');
  $: autoFixableIssues = fixes.filter(fix => fix.canAutoFix).length;
</script>

<div class="question-validator" class:has-errors={hasErrors} class:has-warnings={hasWarnings}>
  <div class="validator-header" on:click={toggleExpanded} role="button" tabindex="0">
    <div class="validator-status">
      {#if hasErrors}
        <span class="status-icon error">❌</span>
        <span class="status-text error">Invalid Configuration</span>
      {:else if hasWarnings}
        <span class="status-icon warning">⚠️</span>
        <span class="status-text warning">Issues Found</span>
      {:else}
        <span class="status-icon success">✅</span>
        <span class="status-text success">Valid</span>
      {/if}
    </div>
    
    <div class="validator-summary">
      {#if validationSummary}
        <span class="relationship-type">{validationSummary.relationshipType}</span>
        <span class="question-type">{validationSummary.questionType}</span>
      {/if}
    </div>
    
    <div class="validator-actions">
      {#if autoFixableIssues > 0 && showAutoFix}
        <button 
          class="btn btn-small btn-warning"
          on:click|stopPropagation={applyAllFixes}
          title="Auto-fix {autoFixableIssues} issue{autoFixableIssues !== 1 ? 's' : ''}"
        >
          Fix ({autoFixableIssues})
        </button>
      {/if}
      <span class="expand-icon" class:expanded>
        {expanded ? '▼' : '▶'}
      </span>
    </div>
  </div>
  
  {#if expanded && validationSummary}
    <div class="validator-details">
      
      <!-- Relationship Summary -->
      <div class="validation-section">
        <h6>Relationship Summary</h6>
        <div class="relationship-info">
          <div class="info-item">
            <strong>Type:</strong> {validationSummary.relationshipType}
          </div>
          <div class="info-item">
            <strong>Description:</strong> {validationSummary.description}
          </div>
          <div class="info-item">
            <strong>Question Type:</strong> {validationSummary.questionType}
          </div>
          <div class="info-item">
            <strong>Expected Encryption:</strong> 
            {#if validationSummary.encryption === null}
              <span class="flexible-encryption">Any encryption allowed</span>
            {:else}
              {JSON.stringify(validationSummary.encryption)}
            {/if}
          </div>
          <div class="info-item">
            <strong>Can be Many-to-Many:</strong> {validationSummary.canBeManyToMany ? 'Yes' : 'No'}
          </div>
        </div>
      </div>
      
      <!-- Issues and Fixes -->
      {#if fixes.length > 0}
        <div class="validation-section">
          <h6>Issues & Fixes</h6>
          <div class="fixes-list">
            {#each fixes as fix}
              <div class="fix-item" class:error={fix.type === 'error'} class:warning={fix.type !== 'error'}>
                <div class="fix-info">
                  <span class="fix-type">{fix.type.toUpperCase()}</span>
                  <span class="fix-message">{fix.message}</span>
                </div>
                {#if fix.canAutoFix && showAutoFix}
                  <button 
                    class="btn btn-small btn-secondary"
                    on:click={() => applyFix(fix)}
                  >
                    Fix
                  </button>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}
      
      <!-- Allowed Types -->
      {#if validationSummary.rules?.allowedTypes}
        <div class="validation-section">
          <h6>Allowed Question Types for {validationSummary.relationshipType}</h6>
          <div class="allowed-types">
            {#each validationSummary.rules.allowedTypes as type}
              <span 
                class="type-badge" 
                class:current={type === validationSummary.questionType}
              >
                {type}
              </span>
            {/each}
          </div>
        </div>
      {/if}
      
      <!-- Compliance Status -->
      <div class="validation-section">
        <h6>Compliance Status</h6>
        <div class="compliance-grid">
          <div class="compliance-item">
            <span class="compliance-label">Type Compatibility:</span>
            <span class="compliance-status" class:success={validationSummary.isValid} class:error={!validationSummary.isValid}>
              {validationSummary.isValid ? '✅ Valid' : '❌ Invalid'}
            </span>
          </div>
          <div class="compliance-item">
            <span class="compliance-label">Encryption:</span>
            <span class="compliance-status" class:success={validationSummary.encryptionCompliant} class:warning={!validationSummary.encryptionCompliant}>
              {#if validationSummary.encryption === null}
                ✅ Flexible
              {:else if validationSummary.encryptionCompliant}
                ✅ Compliant
              {:else}
                ⚠️ Non-compliant
              {/if}
            </span>
          </div>
          <div class="compliance-item">
            <span class="compliance-label">Many-to-Many:</span>
            <span class="compliance-status" class:success={validationSummary.manyToManyCompliant} class:warning={!validationSummary.manyToManyCompliant}>
              {validationSummary.manyToManyCompliant ? '✅ Compliant' : '⚠️ Not supported'}
            </span>
          </div>
        </div>
      </div>
      
    </div>
  {/if}
</div>

<style lang="scss">
  .question-validator {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 20px;
    background: #f9fafb;
    overflow: hidden;
    
    &.has-errors {
      border-color: #ef4444;
      background: #fef2f2;
    }
    
    &.has-warnings {
      border-color: #f59e0b;
      background: #fef3c7;
    }
  }
  
  .validator-header {
    padding: 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
    
    &:focus {
      outline: 2px solid #3b82f6;
      outline-offset: -2px;
    }
  }
  
  .validator-status {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .status-icon {
      font-size: 1rem;
    }
    
    .status-text {
      font-size: 0.8rem;
      font-weight: 600;
      
      &.error {
        color: #dc2626;
      }
      
      &.warning {
        color: #d97706;
      }
      
      &.success {
        color: #059669;
      }
    }
  }
  
  .validator-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .relationship-type {
      background: #3b82f6;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
    
    .question-type {
      background: #6b7280;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
  }
  
  .validator-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .expand-icon {
      font-size: 0.8rem;
      color: #6b7280;
      transition: transform 0.2s ease;
      
      &.expanded {
        transform: rotate(0deg);
      }
    }
  }
  
  .validator-details {
    padding: 16px;
    background: #fff;
    border-top: 1px solid #e5e7eb;
  }
  
  .validation-section {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    h6 {
      margin: 0 0 8px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #374151;
    }
  }
  
  .relationship-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    
    .info-item {
      font-size: 0.7rem;
      color: #6b7280;
      
      strong {
        color: #374151;
      }
    }
  }
  
  .fixes-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .fix-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    border-radius: 4px;
    
    &.error {
      background: #fef2f2;
      border: 1px solid #fca5a5;
    }
    
    &.warning {
      background: #fef3c7;
      border: 1px solid #fcd34d;
    }
    
    .fix-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
      
      .fix-type {
        font-size: 9px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #6b7280;
      }
      
      .fix-message {
        font-size: 0.7rem;
        color: #374151;
      }
    }
  }
  
  .allowed-types {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    
    .type-badge {
      background: #e5e7eb;
      color: #6b7280;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: bold;
      letter-spacing: 0.5px;
      
      &.current {
        background: #10b981;
        color: white;
      }
    }
  }
  
  .compliance-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;
    
    .compliance-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.7rem;
      
      .compliance-label {
        color: #6b7280;
        font-weight: 500;
      }
      
      .compliance-status {
        font-weight: 600;
        
        &.success {
          color: #059669;
        }
        
        &.warning {
          color: #d97706;
        }
        
        &.error {
          color: #dc2626;
        }
      }
    }
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-warning {
      background: #f59e0b;
      color: white;
      
      &:hover {
        background: #d97706;
      }
    }
    
    &.btn-small {
      padding: 3px 6px;
      font-size: 9px;
    }
  }
</style>